import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type IndicatorFilterState = {
  search: string;
  serviceIds: string[];
  categories: string[];
  dataAvailability: string[];
  units: string[];
  frequencies: string[];
  territoryIds: string[];
  statuses: string[];
  sources: string[];
  sort: string;
  page: number;
  view: "table" | "cards";
};

function splitCsv(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function useIndicatorFilters() {
  const [params, setParams] = useSearchParams();

  const state: IndicatorFilterState = useMemo(
    () => ({
      search: params.get("q") ?? "",
      serviceIds: splitCsv(params.get("services")),
      categories: splitCsv(params.get("categories")),
      dataAvailability: splitCsv(params.get("availability")),
      units: splitCsv(params.get("units")),
      frequencies: splitCsv(params.get("frequencies")),
      territoryIds: splitCsv(params.get("territories")),
      statuses: splitCsv(params.get("statuses")),
      sources: splitCsv(params.get("sources")),
      sort: params.get("sort") ?? "libelle_asc",
      page: Math.max(0, Number(params.get("page") ?? "0") || 0),
      view: params.get("view") === "cards" ? "cards" : "table",
    }),
    [params],
  );

  const setState = useCallback(
    (patch: Partial<IndicatorFilterState>) => {
      const next = new URLSearchParams(params);
      const merged = { ...state, ...patch };
      if (merged.search) next.set("q", merged.search);
      else next.delete("q");
      const csv = (key: string, values: string[]) => {
        if (values.length) next.set(key, values.join(","));
        else next.delete(key);
      };
      csv("services", merged.serviceIds);
      csv("categories", merged.categories);
      csv("availability", merged.dataAvailability);
      csv("units", merged.units);
      csv("frequencies", merged.frequencies);
      csv("territories", merged.territoryIds);
      csv("statuses", merged.statuses);
      csv("sources", merged.sources);
      next.set("sort", merged.sort);
      if (merged.page > 0) next.set("page", String(merged.page));
      else next.delete("page");
      if (merged.view === "cards") next.set("view", "cards");
      else next.delete("view");
      setParams(next, { replace: true });
    },
    [params, setParams, state],
  );

  const reset = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const graphqlFilters = {
    search: state.search || null,
    serviceIds: state.serviceIds.length ? state.serviceIds : null,
    categories: state.categories.length ? state.categories : null,
    dataAvailability: state.dataAvailability.length ? state.dataAvailability : null,
    units: state.units.length ? state.units : null,
    frequencies: state.frequencies.length ? state.frequencies : null,
    territoryIds: state.territoryIds.length ? state.territoryIds : null,
    statuses: state.statuses.length ? state.statuses : null,
    sources: state.sources.length ? state.sources : null,
  };

  return { state, setState, reset, graphqlFilters, activeCount: params.size };
}
